import React, { useEffect, useRef, useState } from 'react';
import FocusTrap from 'focus-trap-react';
import { AnalyticsAttributesProvider, ChevronLeft, ChevronRight, MarkdownLink, StyledMarkdown, useAnalytics, useBreakpoints, useHeaderFooterContext } from '../../../components';
import { BackButton, Button, ButtonDark, ButtonPrimary, Buttons, Header, Link, LogoWrapper, MenuButton, NavBar, NavLinkContainer, Page, PageContainer, Secondary, Tab, TabList } from './SmallNav.styles';
import { MenuIcon } from './MenuIcon';
import { Logo, PrimaryLink, ProgressBar, SecondaryButton } from '../shared';
import UiNavLink from '../../UiNavLink/UiNavLink';
import UiBanner from '../../UiBanner/UiBanner';
export function SmallNav({ hasScrolled, isLoggedIn, model: { id, content: { goBackText, goToBoardsText, logInText, navigationLabel, progressBar, progressLabel, signUpText, startTransparent, tabs } } }) {
    const [isOpen, setIsOpen] = useState(false);
    const [activeTab, setActiveTab] = useState();
    const { sendTrackEvent } = useAnalytics();
    const analyticsAttributes = { eventContainer: 'mobileNav' };
    const { setHeaderHeight } = useHeaderFooterContext();
    const breakpoints = useBreakpoints();
    const headerRef = useRef(null);
    function close() {
        setIsOpen(false);
        setTimeout(() => setActiveTab(undefined), 300);
    }
    function escPress(event) {
        if (event.key === 'Escape') {
            close();
        }
    }
    function handleResize() {
        if (headerRef.current && !breakpoints.includes('lg')) {
            const height = headerRef.current.offsetHeight;
            setHeaderHeight(height);
        }
    }
    function open() {
        sendTrackEvent('clickedMobileNavMenu', Object.assign(Object.assign({}, analyticsAttributes), { event: 'clicked', eventComponent: 'mobileNavMenu', label: 'menu open', uuid: `${id}_mobileMenu` }));
        setIsOpen(true);
    }
    useEffect(() => {
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [headerRef, isLoggedIn, setHeaderHeight, breakpoints]);
    useEffect(() => {
        if (isOpen) {
            window.addEventListener('keydown', escPress);
            document.body.classList.add('prevent-scroll');
        }
        return () => {
            window.removeEventListener('keydown', escPress);
            document.body.classList.remove('prevent-scroll');
        };
    }, [isOpen]);
    return (React.createElement(AnalyticsAttributesProvider, { value: analyticsAttributes },
        React.createElement(FocusTrap, { active: isOpen, focusTrapOptions: {
                fallbackFocus: '#skip-to-content'
            } },
            React.createElement(Header, { "data-scrolled": !isOpen && hasScrolled, "data-open": isOpen, "data-testid": "smallnav", startTransparent: startTransparent },
                React.createElement(NavBar, { ref: headerRef },
                    React.createElement(LogoWrapper, { "data-hidden": activeTab !== undefined },
                        React.createElement(Logo, { id: `${id}_mobile` })),
                    React.createElement(BackButton, { "data-hidden": activeTab === undefined || !isOpen, onClick: () => {
                            sendTrackEvent('clickedMobileNavBackButton', {
                                event: 'clicked',
                                eventComponent: 'mobileNavBackButton',
                                eventContainer: 'mobileNav',
                                label: goBackText,
                                uuid: `${id}_back`
                            });
                            setActiveTab(undefined);
                        }, tabIndex: activeTab === undefined || !isOpen ? -1 : undefined },
                        React.createElement(ChevronLeft, null),
                        goBackText),
                    React.createElement(MenuButton, { "aria-expanded": isOpen, "aria-label": navigationLabel, onClick: () => (isOpen ? close() : open()), "data-testid": "menubutton" },
                        React.createElement(MenuIcon, { isOpen: isOpen }),
                        React.createElement("span", { hidden: true }, navigationLabel)),
                    progressBar && React.createElement(ProgressBar, { progressLabel: progressLabel })),
                React.createElement(PageContainer, { open: isOpen },
                    React.createElement(Page, { "aria-hidden": !isOpen || activeTab !== undefined, "data-offscreen": activeTab !== undefined ? 'left' : undefined },
                        React.createElement(TabList, null, tabs &&
                            tabs.map((tab, index) => tab.type === 'uiHeaderNavTab' ? (React.createElement(Tab, { key: index, onClick: () => {
                                    sendTrackEvent('clickedMobileNavTab', {
                                        event: 'clicked',
                                        eventComponent: 'mobileNavTab',
                                        eventContainer: 'mobileNav',
                                        label: tab.content.title,
                                        uuid: tab.id
                                    });
                                    setActiveTab(index);
                                }, tabIndex: !isOpen || activeTab !== undefined ? -1 : undefined },
                                tab.content.title,
                                React.createElement(ChevronRight, null))) : (React.createElement(Link, { key: index, href: tab.content.url, onClick: () => sendTrackEvent('clickedMobileNavLink', {
                                    event: 'clicked',
                                    eventComponent: 'mobileNavLink',
                                    eventContainer: 'mobileNav',
                                    href: tab.content.url,
                                    label: tab.content.text,
                                    uuid: tab.id
                                }), tabIndex: !isOpen || activeTab !== undefined ? -1 : undefined }, tab.content.text)))),
                        React.createElement(Buttons, null,
                            isLoggedIn === true && goToBoardsText && (React.createElement(ButtonDark, { href: "/", onClick: () => sendTrackEvent('clickedMobileNavButton', {
                                    event: 'clicked',
                                    eventComponent: 'mobileNavButton',
                                    eventContainer: 'mobileNav',
                                    href: '/',
                                    label: goToBoardsText,
                                    uuid: `${id}_goToBoards`
                                }), tabIndex: !isOpen || activeTab !== undefined ? -1 : undefined }, goToBoardsText)),
                            isLoggedIn === false && logInText && signUpText && (React.createElement(React.Fragment, null,
                                React.createElement(ButtonPrimary, { href: "/signup", onClick: () => sendTrackEvent('clickedMobileNavButton', {
                                        event: 'clicked',
                                        eventComponent: 'mobileNavButton',
                                        eventContainer: 'mobileNav',
                                        href: '/signup',
                                        label: signUpText,
                                        uuid: `${id}_signup`
                                    }), tabIndex: !isOpen || activeTab !== undefined ? -1 : undefined }, signUpText),
                                React.createElement(Button, { href: "/login", onClick: () => sendTrackEvent('clickedMobileNavButton', {
                                        event: 'clicked',
                                        eventComponent: 'mobileNavButton',
                                        eventContainer: 'mobileNav',
                                        href: '/login',
                                        label: logInText,
                                        uuid: `${id}_logIn`
                                    }), tabIndex: !isOpen || activeTab !== undefined ? -1 : undefined }, logInText))))),
                    tabs &&
                        tabs.map((tab, index) => tab.type === 'uiHeaderNavTab' ? (React.createElement(Page, { key: index, "aria-hidden": !isOpen || activeTab !== index, "data-offscreen": activeTab !== index ? 'right' : undefined },
                            React.createElement(AnalyticsAttributesProvider, { value: Object.assign(Object.assign({}, analyticsAttributes), { parentLabel: tab.content.title }) },
                                React.createElement(TabList, null,
                                    tab.content.primaryTitle && (React.createElement("h2", null, tab.content.primaryTitle)),
                                    tab.content.primaryLinks &&
                                        tab.content.primaryLinks.map((link, linkIndex) => (React.createElement(NavLinkContainer, { key: linkIndex },
                                            React.createElement(UiNavLink, { model: link, tabIndex: activeTab !== index ? -1 : undefined })))),
                                    tab.content.primaryLinkUrl &&
                                        tab.content.primaryLinkText && (React.createElement(PrimaryLink, { url: tab.content.primaryLinkUrl, text: tab.content.primaryLinkText, onClick: () => sendTrackEvent('clickedMobileNavPrimaryLink', Object.assign(Object.assign({}, analyticsAttributes), { event: 'clicked', eventComponent: 'mobileNavPrimaryLink', href: tab.content.primaryLinkUrl, label: tab.content.primaryLinkText, parentLabel: tab.content.title, uuid: `${id}_primaryLink` })), tabIndex: activeTab !== index ? -1 : undefined }))),
                                tab.content.primaryBanner && (React.createElement(UiBanner, { model: tab.content.primaryBanner, tabIndex: activeTab !== index ? -1 : undefined })),
                                React.createElement(Secondary, null,
                                    tab.content.secondaryTitle && (React.createElement("h3", null, tab.content.secondaryTitle)),
                                    tab.content.secondaryDescription && (React.createElement(StyledMarkdown, { renderers: {
                                            link: MarkdownLink
                                        } }, tab.content.secondaryDescription)),
                                    tab.content.secondaryButtonText &&
                                        tab.content.secondaryButtonUrl && (React.createElement(SecondaryButton, { url: tab.content.secondaryButtonUrl, text: tab.content.secondaryButtonText, onClick: () => sendTrackEvent('clickedMobileNavSecondaryButton', Object.assign(Object.assign({}, analyticsAttributes), { event: 'clicked', eventComponent: 'mobileNavSecondaryButton', href: tab.content.secondaryButtonUrl, label: tab.content.secondaryButtonText, parentLabel: tab.content.title, uuid: `${id}_secondaryButton` })), tabIndex: activeTab !== index ? -1 : undefined })))))) : null))))));
}
//# sourceMappingURL=SmallNav.js.map